import type { Coffee, CoffeeInput, CoffeePhoto, PortionLogEntry, Impression, BrewInput, BrewFeedEntry, RoasterColor, CafeLog, CafeLogInput, Dripper, DripperInput, Grinder, GrinderInput, BeanMeasurement, BeanMeasurementInput } from "../types";

// DB stores a brew's flavor_tags as a JSON string; normalize it to an array for the UI.
function parseTags(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === "string" && v.trim()) {
    try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
  }
  return [];
}
function normalizeBrew<T extends { flavor_tags?: unknown; frozen?: unknown }>(row: T): T {
  // frozen is stored as 0/1/NULL; the UI works with boolean | null.
  const frozen = row.frozen === 1 || row.frozen === true ? true : row.frozen === 0 || row.frozen === false ? false : null;
  return { ...row, flavor_tags: parseTags(row.flavor_tags), frozen };
}

interface Envelope<T> { data: T | null; error: string | null; }

export interface AdminUser {
  id: number;
  email: string;
  is_admin: boolean;
  created_at: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}),
      ...(init?.headers || {}),
    },
  });
  if (res.status === 401 && !path.includes("/auth/")) {
    if (typeof window !== "undefined" && !location.pathname.startsWith("/login")) {
      location.href = "/login";
    }
    throw new Error("unauthorized");
  }
  const env = await res.json() as Envelope<T>;
  if (env.error || !res.ok) throw new Error(env.error || `HTTP ${res.status}`);
  return env.data as T;
}

export interface ParseUsage { input: number; output: number; reasoning: number; total: number; model: string; cents: number; }

// Like request(), but also returns the parse endpoint's token-usage summary.
async function requestWithUsage<T>(path: string, init?: RequestInit): Promise<{ data: T; usage: ParseUsage | null }> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}),
      ...(init?.headers || {}),
    },
  });
  if (res.status === 401 && !path.includes("/auth/")) {
    if (typeof window !== "undefined" && !location.pathname.startsWith("/login")) location.href = "/login";
    throw new Error("unauthorized");
  }
  const env = await res.json() as Envelope<T> & { usage?: ParseUsage | null };
  if (env.error || !res.ok) throw new Error(env.error || `HTTP ${res.status}`);
  return { data: env.data as T, usage: env.usage ?? null };
}

export const api = {
  // auth
  login: (email: string, password: string) =>
    request<{ ok: true; email: string; is_admin: boolean }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ ok: true; email: string; is_admin: boolean }>("/api/auth/me"),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>("/api/auth/change-password", { method: "POST", body: JSON.stringify({ current, next }) }),

  // admin / users
  listUsers: () => request<AdminUser[]>("/api/admin/users"),
  createUser: (email: string, password: string, is_admin: boolean) =>
    request<AdminUser>("/api/admin/users", { method: "POST", body: JSON.stringify({ email, password, is_admin }) }),
  updateUser: (id: number, body: { email?: string; password?: string; is_admin?: boolean }) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteUser: (id: number) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),

  // coffees
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request<Coffee[]>(`/api/coffees${qs ? `?${qs}` : ""}`);
  },
  get: (id: number) => request<Coffee>(`/api/coffees/${id}`),
  create: (body: CoffeeInput) => request<Coffee>("/api/coffees", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<CoffeeInput>) => request<Coffee>(`/api/coffees/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: number) => request<Coffee>(`/api/coffees/${id}`, { method: "DELETE" }),
  // Permanent hard delete — purges the coffee + its brew/portion/photo history (irreversible).
  destroy: (id: number) => request<{ ok: true }>(`/api/coffees/${id}?hard=1`, { method: "DELETE" }),
  use: (id: number, note?: string) => request<Coffee>(`/api/coffees/${id}/use`, { method: "POST", body: JSON.stringify({ note }) }),
  log: (id: number) => request<PortionLogEntry[]>(`/api/coffees/${id}/log`),

  listPhotos: (id: number) => request<CoffeePhoto[]>(`/api/coffees/${id}/photos`),
  uploadPhoto: async (id: number, file: File) => {
    const fd = new FormData();
    fd.append("photo", file);
    // 90 s ceiling so a stalled cellular upload doesn't hang the Save button
    // forever. The user can retry from the detail page.
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 90_000);
    try {
      return await request<CoffeePhoto>(`/api/coffees/${id}/photos`, { method: "POST", body: fd, signal: ctrl.signal });
    } catch (e) {
      if ((e as { name?: string }).name === "AbortError") {
        throw new Error("upload timed out after 90s — try a smaller photo or stronger signal");
      }
      throw e;
    } finally {
      clearTimeout(t);
    }
  },
  deletePhoto: (coffeeId: number, photoId: number) =>
    request<{ ok: true }>(`/api/coffees/${coffeeId}/photos/${photoId}`, { method: "DELETE" }),
  reorderPhotos: (id: number, ids: number[]) =>
    request<Coffee>(`/api/coffees/${id}/photos`, { method: "PATCH", body: JSON.stringify({ ids }) }),

  // ai
  parseText: (text: string) => requestWithUsage<Partial<CoffeeInput>>("/api/ai/parse-text", { method: "POST", body: JSON.stringify({ text }) }),
  parseImage: (imageBase64: string, mimeType: string) =>
    request<Partial<CoffeeInput>>("/api/ai/parse-image", { method: "POST", body: JSON.stringify({ imageBase64, mimeType }) }),
  parseImages: (images: { imageBase64: string; mimeType: string }[]) =>
    requestWithUsage<Partial<CoffeeInput>>("/api/ai/parse-image", { method: "POST", body: JSON.stringify({ images }) }),
  parseTextAndImages: (text: string, images: { imageBase64: string; mimeType: string }[]) =>
    requestWithUsage<Partial<CoffeeInput>>("/api/ai/parse-image", { method: "POST", body: JSON.stringify({ images, text }) }),
  parseUrl: (url: string) =>
    request<Partial<CoffeeInput>>("/api/ai/parse-url", { method: "POST", body: JSON.stringify({ url }) }),
  parseDripperUrl: (url: string) =>
    request<Partial<DripperInput>>("/api/ai/parse-dripper-url", { method: "POST", body: JSON.stringify({ url }) }),

  // brew log / impressions
  listImpressions: (id: number) =>
    request<Impression[]>(`/api/coffees/${id}/impressions`).then((rows) => rows.map(normalizeBrew)),
  // Logging a brew also consumes one portion; the updated coffee comes back so the UI can
  // reflect the new count (coffee is null only if none was in stock).
  addImpression: (id: number, brew: BrewInput) =>
    request<{ impression: Impression; coffee: Coffee | null }>(`/api/coffees/${id}/impressions`, { method: "POST", body: JSON.stringify(brew) })
      .then((r) => ({ ...r, impression: normalizeBrew(r.impression) })),
  // Edit an existing brew entry (content only — does not change the portion count).
  updateImpression: (coffeeId: number, impressionId: number, brew: BrewInput) =>
    request<Impression>(`/api/coffees/${coffeeId}/impressions/${impressionId}`, { method: "PUT", body: JSON.stringify(brew) })
      .then(normalizeBrew),
  deleteImpression: (coffeeId: number, impressionId: number) =>
    request<{ ok: true }>(`/api/coffees/${coffeeId}/impressions/${impressionId}`, { method: "DELETE" }),
  // cross-coffee brew feed for the Stats page + recipe-intelligence suggestions
  brews: () => request<BrewFeedEntry[]>("/api/brews").then((rows) => rows.map(normalizeBrew)),

  // café log (out-of-home experiences)
  cafeLogs: {
    list: (params: Record<string, string> = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request<CafeLog[]>(`/api/cafe-logs${qs ? `?${qs}` : ""}`);
    },
    get: (id: number) => request<CafeLog>(`/api/cafe-logs/${id}`),
    create: (body: CafeLogInput) => request<CafeLog>("/api/cafe-logs", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: Partial<CafeLogInput>) => request<CafeLog>(`/api/cafe-logs/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    remove: (id: number) => request<{ ok: true }>(`/api/cafe-logs/${id}`, { method: "DELETE" }),
    listPhotos: (id: number) => request<CoffeePhoto[]>(`/api/cafe-logs/${id}/photos`),
    uploadPhoto: async (id: number, file: File) => {
      const fd = new FormData();
      fd.append("photo", file);
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 90_000);
      try {
        return await request<CoffeePhoto>(`/api/cafe-logs/${id}/photos`, { method: "POST", body: fd, signal: ctrl.signal });
      } catch (e) {
        if ((e as { name?: string }).name === "AbortError") {
          throw new Error("upload timed out after 90s — try a smaller photo or stronger signal");
        }
        throw e;
      } finally {
        clearTimeout(t);
      }
    },
    deletePhoto: (logId: number, photoId: number) =>
      request<{ ok: true }>(`/api/cafe-logs/${logId}/photos/${photoId}`, { method: "DELETE" }),
    reorderPhotos: (id: number, ids: number[]) =>
      request<CoffeePhoto[]>(`/api/cafe-logs/${id}/photos`, { method: "PATCH", body: JSON.stringify({ ids }) }),
  },

  // drippers (brewer catalog)
  drippers: {
    list: () => request<Dripper[]>("/api/drippers"),
    create: (body: DripperInput) => request<Dripper>("/api/drippers", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: Partial<DripperInput>) => request<Dripper>(`/api/drippers/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    remove: (id: number) => request<{ ok: true }>(`/api/drippers/${id}`, { method: "DELETE" }),
  },

  // grinders (grinder catalog)
  grinders: {
    list: () => request<Grinder[]>("/api/grinders"),
    create: (body: GrinderInput) => request<Grinder>("/api/grinders", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: Partial<GrinderInput>) => request<Grinder>(`/api/grinders/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    remove: (id: number) => request<{ ok: true }>(`/api/grinders/${id}`, { method: "DELETE" }),
  },

  // bean size measurements
  beanMeasurements: {
    list: (coffeeId: number) => request<BeanMeasurement[]>(`/api/coffees/${coffeeId}/bean-measurements`),
    create: (coffeeId: number, m: BeanMeasurementInput) =>
      request<{ measurement: BeanMeasurement; coffee: Coffee | null }>(
        `/api/coffees/${coffeeId}/bean-measurements`, { method: "POST", body: JSON.stringify(m) }
      ),
  },

  // roaster colors
  listRoasterColors: () => request<RoasterColor[]>("/api/roaster-colors"),
  setRoasterColor: (roaster: string, bg_color: string, text_color: string, bold: boolean, italic: boolean) =>
    request<RoasterColor>("/api/roaster-colors", { method: "PUT", body: JSON.stringify({ roaster, bg_color, text_color, bold, italic }) }),
  deleteRoasterColor: (roaster: string) =>
    request<{ ok: true }>(`/api/roaster-colors?roaster=${encodeURIComponent(roaster)}`, { method: "DELETE" }),
};
